import React, { Fragment, useContext, useState } from "react";
import {
    EmailShareButton,
    FacebookShareButton,
    LinkedinShareButton,
    TwitterShareButton,
} from "react-share";

import { Context } from "@reactivated";

import { assertNever } from "../../utils/never";
import { concatClassNames } from "../../utils/styles";
import { IntrinsicElementProps } from "../../utils/types";
import { Clickable } from "../Clickable";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

export const shareOptions = [
    "facebook",
    "twitter",
    "linkedin",
    "envelope",
    "link",
] as const;

export type ShareOption = (typeof shareOptions)[number];

export function shareOptionExists(name: string): name is ShareOption {
    return !!shareOptions.find((n) => n === name);
}

export const iconNameMap: {
    [key in ShareOption]: string;
} = {
    twitter: "Twitter",
    facebook: "Facebook",
    linkedin: "LinkedIn",
    envelope: "Email",
    link: "Copy Link",
};

const CopyButton = (props: {
    url: string;
    children?: React.ReactNode;
    attrs?: Omit<
        IntrinsicElementProps<HTMLButtonElement | HTMLAnchorElement>,
        "ref"
    >;
    successClassName?: string;
    failureClassName?: string;
}) => {
    const [state, setState] = useState<"default" | "success" | "failure">(
        "default",
    );
    const attrs = props.attrs || {};
    attrs.className = concatClassNames([
        styles.shareIcon,
        props.attrs?.className || "",
    ]);

    const copyUrl = () => {
        navigator.clipboard
            .writeText(props.url)
            .then(() => {
                setState("success");
                setTimeout(() => setState("default"), 2000);
            })
            .catch((error) => {
                console.log(error);
                setState("failure");
                setTimeout(() => setState("default"), 2000);
            });
    };

    return (
        <Clickable
            title="Copy URL"
            onClick={copyUrl}
            {...attrs}
            className={concatClassNames([
                attrs.className,
                state === "success"
                    ? props.successClassName ?? styles.socialButtonSuccess
                    : "",
                state === "failure"
                    ? props.failureClassName ?? styles.socialButtonFailure
                    : "",
            ])}
        >
            {props.children}
        </Clickable>
    );
};

const ShareButton = (props: {
    site: ShareOption;
    attrs?: Omit<
        IntrinsicElementProps<HTMLButtonElement | HTMLAnchorElement>,
        "ref"
    >;
    successClassName?: string;
    failureClassName?: string;
}) => {
    const context = useContext(Context);
    const url = context.request.url;

    const title = typeof document !== "undefined" ? document.title : "";

    const attrs = props.attrs || {};
    attrs.className = concatClassNames([
        styles.shareIcon,
        props.attrs?.className || "",
    ]);

    const icon = (
        <Svg
            data-icon={props.site}
            name={props.site}
            visuallyHiddenText={iconNameMap[props.site]}
        />
    );

    switch (props.site) {
        case "facebook":
            return (
                <FacebookShareButton
                    resetButtonStyle={false}
                    {...attrs}
                    url={url}
                    title="Share on Facebook"
                >
                    {icon}
                </FacebookShareButton>
            );
        case "twitter":
            return (
                <TwitterShareButton
                    resetButtonStyle={false}
                    {...attrs}
                    url={url}
                    title={title}
                >
                    {icon}
                </TwitterShareButton>
            );
        case "linkedin":
            return (
                <LinkedinShareButton
                    resetButtonStyle={false}
                    {...attrs}
                    url={url}
                    title={title}
                >
                    {icon}
                </LinkedinShareButton>
            );
        case "envelope":
            return (
                <EmailShareButton
                    resetButtonStyle={false}
                    url={url}
                    subject={title}
                    title="Email"
                    {...attrs}
                >
                    {icon}
                </EmailShareButton>
            );
        case "link":
            return (
                <CopyButton
                    url={url}
                    attrs={{ ...attrs, title: "Copy URL" }}
                    successClassName={props.successClassName}
                    failureClassName={props.failureClassName}
                >
                    {icon}
                </CopyButton>
            );
    }
    // Make sure we didn't forget to handle anything
    assertNever(props.site);
};

export type SocialMediaShareBlock = {
    type: "items";
    value: { site: string | null };
    id: string;
}[];

interface Props {
    value: SocialMediaShareBlock | undefined;
    /**
     * Used on every share button
     */
    buttonAttrs?: Omit<
        IntrinsicElementProps<HTMLButtonElement | HTMLAnchorElement>,
        "ref"
    >;
    successClassName?: string;
    failureClassName?: string;
}

export const SocialMediaShare = (props: Props) => {
    if (!props.value) return <></>;
    return (
        <>
            {props.value.map((item, index) => {
                if (!item.value.site || !shareOptionExists(item.value.site))
                    return <Fragment key={`${index}`}></Fragment>;
                return (
                    <ShareButton
                        key={`${item.value.site}-${index}`}
                        site={item.value.site}
                        attrs={props.buttonAttrs}
                        successClassName={props.successClassName}
                        failureClassName={props.failureClassName}
                    />
                );
            })}
        </>
    );
};
